import {
  Activity,
  AlignLeft,
  ArrowRightLeft,
  AtSign,
  BarChart2,
  Bell,
  Bluetooth,
  BookOpen,
  Brain,
  Camera,
  CheckSquare,
  CloudSun,
  Code2,
  Coins,
  Combine,
  Cpu,
  Database,
  Eye,
  FileBarChart,
  FileScan,
  FileText,
  FileUp,
  Film,
  Github,
  GitPullRequest,
  Globe,
  Hammer,
  Hash,
  Image as ImageIcon,
  Inbox,
  Layers,
  Layout,
  Lightbulb,
  Link,
  Link2,
  ListChecks,
  Lock,
  Mail,
  Map,
  MapPin,
  MapPinCheckInside,
  Maximize2,
  MessageCircle,
  MessageSquare,
  Mic,
  MicOff,
  Moon,
  Music,
  Package,
  Paintbrush,
  PenTool,
  Phone,
  PhoneCall,
  Plug,
  RefreshCw,
  Rss,
  Scale,
  ScanSearch,
  ScanText,
  Scroll,
  Search,
  Server,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Speaker,
  StickyNote,
  Target,
  Terminal,
  Trash2,
  Trello,
  UserRoundSearch,
  Volume,
  Volume2,
  Zap,
  type LucideIcon,
} from 'lucide-react';

// Próprias = skills que vivem no workspace OpenClaw da Ruralplan
// (/root/.openclaw/...), criadas/curadas pelo time. Skills de terceiros
// vêm do pacote openclaw em /usr/lib/node_modules/openclaw/skills.
export function isOwnSkill(skill: { skillsDir?: string | null | undefined }): boolean {
  return (skill.skillsDir ?? '').startsWith('/root/.openclaw/');
}

// Mapa skill → ícone Lucide. Skills sem mapeamento usam Zap.
const SKILL_ICON_MAP: Record<string, LucideIcon> = {
  // Próprias Ruralplan
  'hub-client': Server,
  'hub-documentos-upload': FileUp,
  'web-search': Globe,
  'monitor-credito-rural': Coins,
  'monitor-regulatorio': Map,
  'operacao-fundiaria-hub': MapPin,
  'bridge-qdrant': Database,
  'pgvector-search': ScanSearch,
  'geo-spatial': MapPinCheckInside,
  'cross-search': Combine,
  'knowledge-gap': Lightbulb,
  'context-assembler': Layers,
  'cliente-360': UserRoundSearch,
  'embedding-updater': RefreshCw,
  'smart-intake-trigger': ScanText,
  'searxng-search': Search,
  'processo-status': GitPullRequest,
  'mensagem-cliente': MessageCircle,
  'indexar-certidao': FileScan,
  'analise-onus-gravames': Scale,
  'aprendizado-hibrido': Brain,
  'memory-curator': Trash2,
  'dreams-distill': Moon,
  // Segurança & senhas
  '1password': Lock,
  healthcheck: ShieldCheck,
  // IA & modelos
  'coding-agent': Code2,
  gemini: Sparkles,
  oracle: Cpu,
  'model-usage': BarChart2,
  'skill-creator': Hammer,
  clawhub: Package,
  // Busca & web
  'ddg-web-search': Search,
  blogwatcher: Rss,
  xurl: AtSign,
  // Notas & escrita
  'apple-notes': StickyNote,
  'bear-notes': PenTool,
  obsidian: BookOpen,
  notion: Layout,
  'nano-pdf': FileText,
  summarize: AlignLeft,
  // Tarefas & produtividade
  'apple-reminders': Bell,
  'things-mac': ListChecks,
  taskflow: CheckSquare,
  'taskflow-inbox-triage': Inbox,
  trello: Trello,
  'proactive-tasks': Target,
  // Comunicação
  discord: MessageCircle,
  slack: Hash,
  himalaya: Mail,
  imsg: MessageSquare,
  bluebubbles: MessageCircle,
  wacli: Phone,
  'voice-call': PhoneCall,
  'voice-reply': Mic,
  // Código & infra
  github: Github,
  'gh-issues': GitPullRequest,
  tmux: Terminal,
  'node-connect': Link,
  mcporter: Plug,
  // Qdrant
  'qdrant-clients-sdk': Code2,
  'qdrant-deployment-options': Server,
  'qdrant-ingestion-best-practices': FileUp,
  'qdrant-integration': Link2,
  'qdrant-model-migration': ArrowRightLeft,
  'qdrant-monitoring': Activity,
  'qdrant-performance-optimization': Zap,
  'qdrant-scaling': Maximize2,
  'qdrant-search-quality': Search,
  'qdrant-version-upgrade': RefreshCw,
  // Áudio & voz
  'openai-whisper': MicOff,
  'openai-whisper-api': Mic,
  sag: Volume2,
  'sherpa-onnx-tts': Volume,
  songsee: Music,
  sonoscli: Speaker,
  blucli: Bluetooth,
  'spotify-player': Music,
  // Vídeo & imagem
  'video-frames': Film,
  'openai-image-gen': ImageIcon,
  gifgrep: Film,
  camsnap: Camera,
  peekaboo: Eye,
  canvas: Paintbrush,
  // Dispositivos & casa
  openhue: Lightbulb,
  eightctl: Moon,
  // Logs & sessões
  'session-logs': Scroll,
  'session-memory': Database,
  'agent-memory': Brain,
  'memory-hygiene': Trash2,
  'weekly-report-generator': FileBarChart,
  // Outros
  weather: CloudSun,
  ordercli: ShoppingCart,
};

export function resolveSkillIcon(name: string): LucideIcon {
  return SKILL_ICON_MAP[name] ?? Zap;
}

// Descrições PT-BR. Skills sem entrada caem na description vinda do back.
export const SKILL_DESC_PTBR: Record<string, string> = {
  healthcheck:
    'Auditoria de segurança do servidor — firewall, SSH, atualizações e exposição de risco.',
  'openai-image-gen': 'Gera imagens em lote via API OpenAI com galeria HTML automática.',
  'openai-whisper-api': 'Transcreve áudios para texto via API Whisper da OpenAI.',
  'skill-creator': 'Cria e melhora skills do agente — escreve SKILL.md e estrutura o diretório.',
  tmux:
    'Controla sessões tmux remotas — envia teclas e captura saída de terminais interativos.',
  'video-frames': 'Extrai frames e clipes de vídeos usando ffmpeg.',
  weather: 'Consulta clima e previsão do tempo para qualquer cidade via wttr.in.',
  'memory-hygiene':
    'Audita, limpa e otimiza a memória vetorial. Use quando a memória estiver sobrecarregada.',
  'session-memory':
    'Memória persistente entre sessões — salva fatos, busca por relevância, consolida insights.',
  'agent-memory':
    'Memória estruturada com SQLite — entidades, fatos e lições aprendidas com busca por contexto.',
  'voice-reply': 'Responde em áudio via OpenAI TTS e envia como nota de voz no Telegram.',
  '1password': 'Integração com 1Password via CLI — credenciais sem expor valores.',
  clawhub: 'Busca, instala, atualiza e publica skills no repositório ClawHub.',
  'coding-agent': 'Delega tarefas de código para agentes especializados em sessões de background.',
  discord: 'Envio e recebimento de mensagens no Discord.',
  gemini: 'Integração com Gemini CLI para perguntas rápidas, resumos e geração de conteúdo.',
  'gh-issues': 'Busca issues no GitHub, cria pull requests e monitora revisões.',
  github: 'Operações no GitHub via CLI: issues, PRs, CI, code review.',
  himalaya: 'Gerencia e-mails via IMAP/SMTP — lista, lê, escreve e responde.',
  mcporter: 'Conecta e chama servidores MCP externos diretamente.',
  'model-usage': 'Resumo de custo e uso por modelo de IA.',
  'nano-pdf': 'Edita PDFs com instruções em linguagem natural.',
  'node-connect':
    'Diagnostica falhas de conexão de dispositivos Android/iOS/macOS com OpenClaw.',
  notion: 'Integração com Notion via API — páginas, bancos de dados e blocos.',
  obsidian: 'Trabalha com vaults do Obsidian e automatiza via CLI.',
  'openai-whisper': 'Transcrição de áudio localmente via Whisper CLI, sem API key.',
  sag: 'Texto para voz via ElevenLabs.',
  'session-logs': 'Pesquisa logs de conversas anteriores para recuperar contexto.',
  slack: 'Controla o Slack — envia mensagens, lê canais e gerencia notificações.',
  summarize: 'Resume URLs, podcasts, arquivos e vídeos do YouTube — saída vai pra memória.',
  xurl: 'Requisições autenticadas à API do X (Twitter) via CLI.',
  'proactive-tasks':
    'Executa metas e tarefas nos heartbeats sem ser acionado — reporta progresso.',
  'weekly-report-generator': 'Gera relatório semanal com KPIs, wins, bloqueios e prioridades.',
  'ddg-web-search': 'Pesquisa web via DuckDuckGo sem API key.',
  'web-search':
    'Busca web via SearXNG self-hosted (Google + Bing + DuckDuckGo). Compartilhada entre agentes.',
  'monitor-credito-rural':
    'Monitora publicações de crédito rural (PRONAF, PRONAMP, MCR, Plano Safra).',
  'monitor-regulatorio':
    'Monitora publicações regulatórias (DOU, INCRA, IBAMA, CONAMA, SEMA-RO).',
  'operacao-fundiaria-hub':
    'Gestão de operações fundiárias no Hub — processos, documentos e clientes.',
  'hub-client':
    'Integração com a API do Hub — clientes, imóveis e dados cadastrais internos.',
  'hub-documentos-upload':
    'Upload de documentos (CCIR, CAR, escrituras) e vinculação a cliente/imóvel.',
  'bridge-qdrant':
    'Indexa e sincroniza documentos no Qdrant — base vetorial dos agentes atualizada.',
  'pgvector-search':
    'Busca semântica no PostgreSQL via pgvector — clientes, imóveis e documentos por similaridade.',
  'geo-spatial':
    'Operações geoespaciais — sobreposições, distâncias e polígonos via PostGIS.',
  'cross-search':
    'Busca cruzada entre Qdrant, pgvector e PostgreSQL — combina resultados.',
  'knowledge-gap': 'Detecta lacunas no conhecimento dos agentes e sugere indexações.',
  'context-assembler':
    'Agrega dados de cliente, imóvel, crédito e regulatório antes de responder.',
  'cliente-360':
    'Visão completa do cliente rural — documentos, processos, crédito e alertas regulatórios.',
  'embedding-updater':
    'Regenera embeddings NULL ou desatualizados nas tabelas do Hub.',
  'indexar-certidao':
    'Indexa certidões (matrícula, CCIR, ITR, licença, CAR) com OCR + chunks no Qdrant.',
  'smart-intake-trigger':
    'Dispara o Smart Intake — OCR + GPT-4o-mini Vision + GPT-5 cria cliente + imóveis.',
  'searxng-search': 'Pesquisa web via SearXNG self-hosted.',
  'processo-status':
    'Status de processos GEO/CCIR/Titulação — coluna Kanban, SLA, responsável, protocolo.',
  'mensagem-cliente':
    'Texto padronizado (WhatsApp/email/formal) para informar cliente sobre andamento.',
  'analise-onus-gravames':
    'Parecer registral de matrícula — classifica ônus e gravames, identifica passíveis de baixa e avalia aptidão para garantia em crédito rural.',
  'aprendizado-hibrido':
    'Transforma correções, erros recorrentes e padrões repetidos em memória útil — atualiza hot.json, daily ou Qdrant sem duplicar MEMORY.md.',
  'memory-curator':
    'Curadoria proativa da memória do agente — promove memória madura, poda estagnada e reconcilia contradições entre camadas (memory/, hot.json, Qdrant).',
  'dreams-distill':
    'Destila DREAMS.md (diário poético) — agrupa entradas por mês, identifica temas e sugere arquivar meses antigos preservando o conteúdo bruto.',
};

export function resolveSkillDescription(skill: { name: string; description?: string | null | undefined }): string {
  return SKILL_DESC_PTBR[skill.name] ?? skill.description ?? 'Sem descrição.';
}
